import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import axios from "axios"

/* --------------------------------- Voucher Stats ------------------------------ */

interface BrandData {
  name: string;
  amount: number;
}

interface TransactionData {
  date: string;
  amount: number;
}

interface VoucherStatsResponse {
  data: {
  records: number;
  stats: {
    issued: number;
    activated: number;
    used: number;
    revoked: number;
    failed: number;
    expired: number;
  };
  brandsData: BrandData[];
  transactionData: TransactionData[];
  }
}


export interface VoucherStatsParams {
  fromDate: string;
  toDate: string;
  page?: number;
  limit?: number | null;
  dateField?: "createdAt" | "voucherCreateDate" | "expiryDate";
  program?: number | number[];
  distributor?: number | number[];
  channel?: number | number[];
  processor?: number | number[];
  state?: string | string[];
  recon?: number | number[];
  search?: string;
  timezone?: string;
}

export const fetchVoucherStats = createAsyncThunk(
  "distributor/fetchVoucherStats",
  async (params: VoucherStatsParams, { rejectWithValue }) => {
    const token = localStorage.getItem('accessToken')
    try {
      const {
        fromDate,
        toDate,
        page,
        limit,
        dateField,
        program,
        distributor,
        channel,
        processor,
        state,
        recon,
        search,
        timezone,
      } = params;

      const requestBody: Record<string, unknown> = {
        fromDate,
        toDate,
        ...(timezone && { timezone }),
        ...(page != null && { page }),
        ...(limit != null && { limit }),
      };
      if (dateField) requestBody.dateField = dateField;
      if (program != null) requestBody.program = program;
      if (distributor != null) requestBody.distributor = distributor;
      if (channel != null) requestBody.channel = channel;
      if (processor != null) requestBody.processor = processor;
      if (state) requestBody.state = state;
      if (recon != null) requestBody.recon = recon;
      if (search) requestBody.search = search;

      const response = await axios.request({
        baseURL: `${window.env.API_URL}/api/v1/voucher-requests/voucher-stats`,
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: requestBody,
        method: "POST",
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch voucher statistics"
      );
    }
  }
);

/* --------------------------------- Main State ------------------------------ */

interface DashboardState {
  dashboardLoading: boolean
  dashboardError: any
  voucherStats: VoucherStatsResponse['data']['stats'] | null;
  brandsData: BrandData[] | null;
  transactionData: TransactionData[] | null;
  records: number | null;
}

const initialState: DashboardState = {
  dashboardLoading: false,
  dashboardError: null,
  voucherStats: null,
  brandsData: null,
  transactionData: null,
  records: null,
};

const dashboardSlice = createSlice({
  name: "distributor",
  initialState,
  reducers: {
    clearDashboard(state) {
      state.dashboardLoading = false
      state.dashboardError = null
      state.voucherStats = null
      state.brandsData = null
      state.transactionData = null
    },
  },
  extraReducers: builder => {
    builder

      // Voucher Stats
      .addCase(fetchVoucherStats.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchVoucherStats.fulfilled, (state, action: PayloadAction<VoucherStatsResponse>) => {
        state.dashboardLoading = false;
        state.voucherStats = action.payload.data.stats;
        state.brandsData = action.payload.data.brandsData;
        state.transactionData = action.payload.data.transactionData;
        state.records = action.payload.data.records;
      })
      .addCase(fetchVoucherStats.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload || "Failed to fetch voucher statistics";
      })
  },
})

export const { clearDashboard } = dashboardSlice.actions

export default dashboardSlice.reducer
